import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import TopNav from '../components/feature/TopNav';
import { loadAdvisorEssentialData, loadAdvisorDirectoryData } from '../services/advisorApiAdapter';
import ActivityInputBar from '../components/feature/ActivityInputBar';
import CustomerDetailsCard from './customer-profile/components/CustomerDetailsCard';
import ActiveCaseSummary from './customer-profile/components/ActiveCaseSummary';
import FamilyMembersCases from './customer-profile/components/FamilyMembersCases';
import { mvpApi } from '../services/api';
import { matchesHistoryFilter, mergeCaseHistoryEvents } from '../utils/caseHistoryClient';
import { SkeletonProfilePage, SkeletonHistoryList } from '../components/advisor/SkeletonLoader';
import { readAdvisorCasesListReturn } from '../utils/casesListUrl';

export default function AdvisorCustomerProfile() {
  const { t, i18n } = useTranslation();
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const casesListReturnTo = useMemo(
    () => state?.returnTo || readAdvisorCasesListReturn(),
    [state?.returnTo],
  );
  const [loading, setLoading] = useState(true);

  // Initialize with state data if available for instant feel
  const [users, setUsers] = useState(() => {
    if (state?.user && String(state.user.id) === String(customerId)) {
      return [state.user];
    }
    // If we only have partial info (e.g. from case list), still show the header instantly
    if (state?.userName && String(state.userId || state.user_id) === String(customerId)) {
      return [{ id: customerId, name: state.userName }];
    }
    return [];
  });
  const [cases, setCases] = useState([]);
  const [tab, setTab] = useState('details');
  const [historyFilter, setHistoryFilter] = useState('all');
  /** Merged advisor timeline: customer profile history + each of this user's cases (newest first). */
  const [profileHistoryRaw, setProfileHistoryRaw] = useState([]);
  const [profileHistoryLoading, setProfileHistoryLoading] = useState(false);
  const [profileNotePosting, setProfileNotePosting] = useState(false);
  const [profileHistoryError, setProfileHistoryError] = useState(null);
  const [advisorName, setAdvisorName] = useState('Advisor');
  const lastFetchedHistoryKeyRef = useRef('');
  const dataFetchedIdRef = useRef(null);
  const familyFetchedIdRef = useRef(null);
  const [familyGroup, setFamilyGroup] = useState(null);
  const [familyMemberList, setFamilyMemberList] = useState([]);
  const [familyBusy, setFamilyBusy] = useState(false);
  const [familyNameInput, setFamilyNameInput] = useState('');
  const [followUpSaving, setFollowUpSaving] = useState(false);
  const [inquirySaving, setInquirySaving] = useState(false);

  const refreshFamily = useCallback(async () => {
    if (!customerId || familyFetchedIdRef.current === customerId) return;
    familyFetchedIdRef.current = customerId;
    setFamilyBusy(true);
    try {
      const fgRes = await mvpApi.getFamilyGroupByUser(customerId);
      const fg = fgRes?.data?.family_group || null;
      if (!fg) {
        setFamilyGroup(null);
        setFamilyMemberList([]);
        return;
      }

      setFamilyGroup({
        familyGroupId: fg.family_group_id,
        familyLabel: fg.family_name,
        leadContactUserId: fg.lead_contact_user_id,
      });

      const membersRes = await mvpApi.getFamilyMembers(fg.family_group_id);
      const members = membersRes?.data?.members || [];
      setFamilyMemberList(members);

      // Important: family-group fetch on the backend may create/seed the lead's
      // Mongo session (so they show in "Open cases"). Re-fetch cases to avoid stale UI.
      const casesRes = await mvpApi.getAllUserCases({ page: 1, page_size: 100 });
      const nextCases = casesRes?.data?.cases || [];
      setCases(nextCases);
    } catch {
      setFamilyGroup(null);
      setFamilyMemberList([]);
    } finally {
      setFamilyBusy(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (!customerId || dataFetchedIdRef.current === customerId) return;
    dataFetchedIdRef.current = customerId;

    setLoading(true);
    // Load cases and my profile in parallel with directory, but update UI as soon as each finishes
    loadAdvisorEssentialData().then((essential) => {
      const { cases: c, me } = essential;
      setCases(c);
      const name = `${me?.first_name || ''} ${me?.last_name || ''}`.trim() || me?.name || me?.email;
      if (name) setAdvisorName(name);
    });

    loadAdvisorDirectoryData({ page: 1, page_size: 500 }).then(({ users: u }) => {
      setUsers((prev) => {
        // Merge with existing user (e.g. from state)
        const combined = [...u];
        const stateUser = state?.user;
        if (stateUser && !combined.find(x => String(x.id) === String(stateUser.id))) {
          combined.push(stateUser);
        }
        return combined;
      });
    }).finally(() => {
      setLoading(false);
    });
  }, [customerId]);

  const user = useMemo(() => users.find((u) => String(u.id) === String(customerId)), [users, customerId]);
  const followUpFlag = Boolean(user?.follow_up || user?.followUp);
  const inquiryFlag = Boolean(user?.inquiry);
  const userCases = useMemo(() => cases.filter((c) => String(c.user_id) === String(customerId)), [cases, customerId]);
  useEffect(() => {
    if (!loading) refreshFamily();
  }, [loading, refreshFamily]);

  useEffect(() => {
    setFamilyNameInput('');
  }, [customerId]);

  useEffect(() => {
    if (familyGroup || !user) return;
    const parts = String(user.name || '').trim().split(/\s+/);
    const d = parts.length ? parts[parts.length - 1] : t('advisorPortal.profileFamilyDefaultName');
    setFamilyNameInput(d);
  }, [customerId, familyGroup, user?.id, user?.name, t]);

  const loadProfileHistory = useCallback(async () => {
    if (!customerId) return;
    const casesKey = (userCases || []).map((c) => c.session_id || c.id).sort().join(',');
    const currentKey = `${customerId}:${casesKey}`;

    // Prevent redundant fetches if we already have this data
    if (lastFetchedHistoryKeyRef.current === currentKey) return;

    setProfileHistoryLoading(true);
    setProfileHistoryError(null);
    try {
      const profileRes = await mvpApi.getUserProfileHistory(customerId, {
        params: { history_tab: 'all', limit: 400 },
      });
      const profileEvents = profileRes?.data?.events || [];
      const caseRows = await Promise.all(
        (userCases || []).map((c) =>
          mvpApi
            .getCaseHistory(c.user_id, c.session_id, { params: { history_tab: 'all', limit: 200 } })
            .then((r) => r?.data?.events || [])
            .catch(() => []),
        ),
      );
      const merged = mergeCaseHistoryEvents([...profileEvents, ...caseRows.flat()]);
      setProfileHistoryRaw(merged);
      lastFetchedHistoryKeyRef.current = currentKey;
    } catch {
      setProfileHistoryError(t('advisorPortal.profileCouldNotLoadHistory'));
      setProfileHistoryRaw([]);
    } finally {
      setProfileHistoryLoading(false);
    }
  }, [customerId, userCases, t]);

  useEffect(() => {
    if (!customerId) return;
    loadProfileHistory();
  }, [customerId, loadProfileHistory]);

  const handlePostProfileNote = useCallback(
    async (entry) => {
      if (!customerId) return;
      setProfileNotePosting(true);
      setProfileHistoryError(null);
      try {
        const normalizedKind = entry?.entryInputType || entry?.type || 'internal_note';
        const normalizedText = entry?.text || entry?.detail || '';
        await mvpApi.postUserProfileHistoryNote(customerId, {
          kind: normalizedKind,
          text: normalizedText,
          document_tag: entry.documentTag || undefined,
        });
        lastFetchedHistoryKeyRef.current = ''; // Force refresh after note
        await loadProfileHistory();
      } catch (e) {
        const raw = e?.response?.data?.detail;
        setProfileHistoryError(typeof raw === 'string' ? raw : t('advisorPortal.profileCouldNotPostHistory'));
        throw e;
      } finally {
        setProfileNotePosting(false);
      }
    },
    [customerId, loadProfileHistory, t],
  );

  const filteredProfileHistory = useMemo(() => {
    return profileHistoryRaw.filter((e) => matchesHistoryFilter(e, historyFilter));
  }, [profileHistoryRaw, historyFilter]);

  const defaultFamilyName = useMemo(() => {
    const parts = String(user?.name || '').trim().split(/\s+/);
    return parts.length ? parts[parts.length - 1] : t('advisorPortal.profileFamilyDefaultName');
  }, [user, t]);

  const primaryCaseId = useMemo(() => {
    const c = userCases && userCases.length ? userCases[0] : null;
    if (!c) return '';
    return c.public_case_id || `${c.user_id}__${c.session_id}`;
  }, [userCases]);

  const formatHistoryStamp = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).replace(',', ' ·');
  };

  const createFamilyGroup = async () => {
    if (!customerId) return;
    const label = (familyNameInput || defaultFamilyName).trim() || t('advisorPortal.profileFamilyDefaultName');
    setFamilyBusy(true);
    try {
      await mvpApi.createFamilyGroup(label, customerId);
      await refreshFamily();
    } finally {
      setFamilyBusy(false);
    }
  };

  // Only block the entire page if we don't even have basic user data
  if (loading && !user) {
    return <SkeletonProfilePage />;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <TopNav advisorName={advisorName} />
        <main className="flex-1 flex flex-col items-center justify-center">
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 text-slate-300">
            <i className="ri-user-unfollow-line text-5xl" />
          </div>
          <h2 className="text-lg font-semibold text-slate-700 mb-2">{t('advisorPortal.profileCustomerNotFound')}</h2>
          <Link to="/advisor" className="text-sm text-emerald-600 hover:text-emerald-800 transition-colors cursor-pointer">
            {t('advisorPortal.newCustomerBackSearch')}
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <TopNav advisorName={advisorName} />
      <main className="flex-1 px-4 sm:px-8 py-8 max-w-screen-2xl mx-auto w-full">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-400 mb-5">
          <button onClick={() => navigate(casesListReturnTo)} type="button" className="flex items-center gap-1 text-slate-500 hover:text-slate-800 transition-colors cursor-pointer mr-1">
            <i className="ri-arrow-left-line text-sm" />
            <span>{t('advisorPortal.profileBackShort')}</span>
          </button>
          <span className="text-slate-300">/</span>
          <Link to={casesListReturnTo} className="hover:text-slate-700 transition-colors cursor-pointer">
            {t('advisorPortal.profileBreadcrumbApplications')}
          </Link>
          {familyGroup ? (
            <>
              <i className="ri-arrow-right-s-line" />
              <span className="text-slate-500">{t('advisorPortal.profileBreadcrumbFamily')}</span>
              <i className="ri-arrow-right-s-line" />
              <span className="text-slate-600 font-medium font-mono truncate max-w-[140px]">{familyGroup.familyLabel}</span>
            </>
          ) : null}
          <i className="ri-arrow-right-s-line" />
          <span className="text-slate-800 font-semibold truncate max-w-[200px]">
            {user.name || t('advisorPortal.profileCustomerFallback')}
          </span>
        </div>

        {familyBusy ? (
          <div className="flex items-center gap-2 text-sm text-slate-600 bg-white border border-slate-200 rounded-xl px-4 py-3 mb-5">
            <i className="ri-loader-4-line animate-spin" />
            {t('advisorPortal.profileUpdatingFamilyGroup')}
          </div>
        ) : null}

        {familyGroup && familyGroup.leadContactUserId != null && String(familyGroup.leadContactUserId) === String(customerId) ? (
          <div className="flex items-center gap-2.5 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 mb-5">
            <i className="ri-star-line text-emerald-700" />
            <p className="text-sm text-emerald-900 font-medium">{t('advisorPortal.profilePrimaryContactNow')}</p>
          </div>
        ) : null}

        <div className="flex items-start gap-4 mb-6">
          <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
            <span className="text-lg font-bold text-emerald-700">
              {String(user.name || 'CU')
                .split(/\s+/)
                .filter(Boolean)
                .slice(0, 2)
                .map((p) => p[0]?.toUpperCase() || '')
                .join('')}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-semibold text-slate-900">
              {user.name || t('advisorPortal.profileCustomerFallback')}
            </h1>
            {Boolean(user.family_interest || user.interested_in_family) ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5 mt-1">
                <i className="ri-group-line" />
                Interested in adding family
              </span>
            ) : null}
          </div>
        </div>

        <div className="flex items-center gap-1 border-b border-slate-200 mb-6">
          <button onClick={() => setTab('details')} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap border-b-2 -mb-px ${tab === 'details' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`}>
            Details
          </button>
          <button onClick={() => setTab('history')} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap border-b-2 -mb-px ${tab === 'history' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`}>
            History
          </button>
        </div>

        {tab === 'details' && (
          <div className="flex flex-col lg:grid lg:grid-cols-5 gap-6 items-start">
            <div className="lg:col-span-2 space-y-5 w-full">
              <CustomerDetailsCard
                customer={user}
                onCustomerUpdated={(updated) =>
                  setUsers((prev) =>
                    prev.map((x) => (String(x.id) === String(updated.id) ? { ...x, ...updated } : x)),
                  )
                }
              />
              {!familyGroup ? (
                <div className="bg-white border border-slate-200 rounded-xl p-5">
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-slate-800">
                        {t('advisorPortal.profileFamilyGroupTitle')}
                      </h3>
                      <p className="text-xs text-slate-400 mt-1">
                        {t('advisorPortal.profileFamilyNotLinked')}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={createFamilyGroup}
                      disabled={familyBusy}
                      className="shrink-0 inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 border border-slate-300 bg-slate-50 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <i className="ri-node-tree text-sm" />
                      {familyBusy ? t('advisorPortal.profileCreatingFamily') : t('advisorPortal.profileCreateFamilyGroup')}
                    </button>
                  </div>
                </div>
              ) : null}
              <FamilyMembersCases
                familyGroup={familyGroup}
                currentCustomerId={customerId}
                members={familyMemberList}
                onFamilyUpdated={refreshFamily}
              />
            </div>
            <div className="lg:col-span-3 space-y-5 w-full">
              <div className="bg-white border border-slate-200 rounded-xl">
                <div className="flex items-center gap-2.5 px-5 py-4 border-b border-slate-100 bg-slate-50/60 rounded-t-xl">
                  <div className="w-3.5 h-3.5 rounded-[4px] bg-slate-400 shrink-0" />
                  <h2 className="text-sm font-semibold text-slate-800">Add Note</h2>
                </div>
                <div className="px-4 py-3">
                  <ActivityInputBar
                    onPost={handlePostProfileNote}
                    advisorName={advisorName}
                    posting={profileNotePosting}
                    hideTypeSelector
                  />
                </div>
              </div>
              <ActiveCaseSummary customer={user} cases={userCases} />
            </div>
          </div>
        )}

        {tab === 'history' && (
          <div className="space-y-4">
            <div className="bg-white border border-slate-200 rounded-xl p-3">
              <ActivityInputBar
                advisorName={advisorName}
                posting={profileNotePosting}
                onPost={handlePostProfileNote}
              />
              {profileHistoryError ? (
                <p className="text-xs text-rose-600 mt-2">{profileHistoryError}</p>
              ) : null}
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-3.5 h-3.5 rounded-[4px] bg-slate-400 shrink-0" />
                <h3 className="text-sm font-semibold text-slate-800 flex-1">History</h3>
                <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
                  {['all', 'notes', 'activity'].map((f) => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => setHistoryFilter(f)}
                      className={`px-3 py-1 text-xs font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap ${historyFilter === f ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                      {f === 'all' ? 'All' : f === 'notes' ? 'Notes' : 'Activity'}
                    </button>
                  ))}
                </div>
              </div>
              {profileHistoryLoading ? (
                <SkeletonHistoryList rows={5} />
              ) : null}
              {!filteredProfileHistory.length && !profileHistoryLoading ? (
                <p className="text-sm text-slate-400 py-6 text-center">{t('advisorPortal.profileNoEventsMatch')}</p>
              ) : null}
              {filteredProfileHistory.length ? (
                <div className="space-y-1">
                  {filteredProfileHistory.map((e, i) => {
                    const isLast = i === filteredProfileHistory.length - 1;
                    return (
                      <div key={e.id || `${e.at}-${i}`} className="flex gap-3">
                        <div className="flex flex-col items-center shrink-0">
                          <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-slate-100">
                            <i className="ri-sticky-note-line text-xs text-slate-500" />
                          </div>
                          {!isLast ? <div className="w-px flex-1 bg-slate-100 mt-1 min-h-[16px]" /> : null}
                        </div>
                        <div className="pb-4 flex-1 min-w-0">
                          <div className="flex items-start gap-2 justify-between">
                            <div className="flex items-center gap-2 flex-wrap min-w-0">
                              <span className="text-sm font-semibold text-slate-700 leading-tight">{e.title || 'History Event'}</span>
                              {primaryCaseId ? (
                                <span className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-medium text-teal-700 bg-teal-50 border border-teal-200 px-1.5 py-0.5 rounded-full">
                                  <i className="ri-price-tag-3-line text-[9px]" />{primaryCaseId}
                                </span>
                              ) : null}
                            </div>
                            <span className="text-[11px] text-slate-400 whitespace-nowrap shrink-0 ml-2">
                              {formatHistoryStamp(e.at)}
                            </span>
                          </div>
                          {e.body ? (
                            <p className="text-xs text-slate-500 mt-1 leading-relaxed whitespace-pre-line">{e.body}</p>
                          ) : null}
                          {e.actor_name ? (
                            <p className="text-[11px] text-slate-400 mt-1">by {e.actor_name}</p>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

